import { useCallback, useEffect, useMemo, useState } from "react";

import { CAMPAIGN_MESSAGE_TYPE_OPTIONS } from "@regal-voice/shared-types/entities";
import { Form } from "antd";
import { clsx } from "clsx";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import { useSelector } from "react-redux";

import { RVButton } from "@regal-voice/ui";
import { PanelTitle } from "Components/shared/PanelTitle/PanelTitle";
import { useToggle } from "Hooks/useToggle";
import { dropOutboundEdges, orphanedEdgeHandler } from "Pages/orchestration/Pages/journeys/Thunks/edgeManager";
import {
    clearEditedNode,
    selectEditedNode,
    selectFormFields,
    selectShouldDropOutboundEdges,
    setFields,
    updateNode,
} from "Services/state/journeys/JourneyStateSlice";
import { useRVDispatch } from "Services/state/Storage";
import { NodeType, Variant } from "Types/Journey";

import { JourneyFormProvider } from "../../../shared/Contexts/JourneyFormContext";
import { useTriggerEventNode } from "../../../shared/hooks/useTriggerEventNode";
import { normalizeIsOneOfPropertyValues } from "../../../shared/node-mappers";
import { AgentlessCallFormItems } from "./Components/_DemoFormItems/AgentlessCallFormItems/AgentlessCallFormItems";
import { CustomAITaskFormItems } from "./Components/_DemoFormItems/CustomAITaskFormItems/CustomAITaskFormItem";
import { DecisionAIFormItems } from "./Components/_DemoFormItems/DecisionAIFormItems/DecisionAIFormItems";
import { EightByEightFormItems } from "./Components/_DemoFormItems/EightByEightFormItems/EightByEightFormItems";
import { EmailTaskFormItems } from "./Components/_DemoFormItems/EmailTaskFormItems/EmailTaskFormItems";
import { Five9FormItems } from "./Components/_DemoFormItems/Five9FormItems/Five9FormItems";
import { GenesysFormItems } from "./Components/_DemoFormItems/GenesysFormItems/GenesysFormItems";
import { NiceFormItems } from "./Components/_DemoFormItems/NiceFormItems/NiceFormItems";
import { RinglessVMFormItems } from "./Components/_DemoFormItems/RinglessVoicemailFormItems/RinglessVoicemailFormItems";
import { SendEmailFormItems } from "./Components/_DemoFormItems/SendEmailFormitems/SendEmailFormItems";
import { SMSAIFormItems } from "./Components/_DemoFormItems/SMSAIFormItems/SMSAIFormItems";
import { TalkdeskFormItems } from "./Components/_DemoFormItems/TalkdeskFormItems/TalkdeskFormItems";
import { VoiceAIFormItems } from "./Components/_DemoFormItems/VoiceAIFormItems/VoiceAIFormItems";
import { ABTestFormItems } from "./Components/ABTestFormItems/ABTestFormItems";
import { AssignTargetAgentFormItems } from "./Components/AssignTargetAgentFormItems/AssignTargetAgentFormItems";
import { AgentSMSTask, CallFormItems, SMSFormItems } from "./Components/CallFormItems/CallFormItems";
import { CancelTaskFormItems } from "./Components/CancelTaskFormItems/CancelTaskFormItems";
import { MultipleCriteriaConditionalNodeItem } from "./Components/ConditionalFormItems/MultipleCriteriaConditionalNodeItem";
import { CustomTaskFormItems } from "./Components/CustomTaskFormItems/CustomTaskFormItems";
import { DelayFormItems } from "./Components/DelayFormItems/DelayFormItems";
import { ReminderFormItems } from "./Components/ReminderFormItems/ReminderFormItems";
import { ScheduledCallbackFormItems } from "./Components/ScheduledCallbackFormItems/ScheduledCallbackFormItems";
import { TriggerFormItems } from "./Components/TriggerFormItems/TriggerFormItems";
import { UpdateContactForm } from "./Components/UpdateContactFormItems/constants";
import { UpdateContactFormItems } from "./Components/UpdateContactFormItems/UpdateContactFormItems";
import { UpdateTaskFormItems } from "./Components/UpdateTaskFormItems/UpdateTaskFormItems";
import { WebhookFormItems } from "./Components/WebhookFormItems/WebhookFormItems";
import { getTitle, removeAfterTouched, unflattenFields } from "./Utils/utils";

import type { FieldData } from "./shared";
import type { FormInstance } from "antd";

import styles from "../JourneyActionsPanel.module.scss";

const UPDATE_CONTACT_FULL_ERROR = "An event trigger or contact attribute update is required.";

type updateFunctionProps = {
    form: FormInstance;
    eventName: string;
    properties: any;
    profileAttributesList: any;
};

const errorCheckForDuplicates = (form: FormInstance, list: object, itemName: string, errorMessage: string) => {
    // @ts-expect-error need better typing
    const names = Object.keys(list || {}).reduce((acc, key) => {
        // @ts-expect-error need better typing
        const name = list[key][itemName];
        return [...acc, name];
    }, []);
    const uniqueNames = new Set(names);
    if (uniqueNames.size !== names.length) {
        form.setFields([
            {
                name: UpdateContactForm.updateContactError,
                errors: [errorMessage],
            },
        ]);
        return true;
    }
    return false;
};
const updateContactNodeIsInvalid = ({
    form,
    eventName,
    properties,
    profileAttributesList,
}: updateFunctionProps): boolean => {
    // validate either or exists
    if (!eventName && !profileAttributesList) {
        form.setFields([
            {
                name: UpdateContactForm.updateContactError,
                errors: [UPDATE_CONTACT_FULL_ERROR],
            },
        ]);
        return true;
    }

    if (
        errorCheckForDuplicates(
            form,
            properties,
            UpdateContactForm.propertyName,
            "Duplicate trigger event names are not allowed."
        )
    ) {
        return true;
    }
    if (
        errorCheckForDuplicates(
            form,
            profileAttributesList,
            UpdateContactForm.profileAttrName,
            "Duplicate Contact names are not allowed."
        )
    ) {
        return true;
    }

    return false;
};

export function NodeEditorPanel({
    id,
    type,
    isViewOnly = false,
}: {
    id: string;
    type: string;
    isViewOnly?: boolean;
}): JSX.Element {
    const [form] = Form.useForm();
    const dispatch = useRVDispatch();
    const fields = useSelector(selectFormFields);
    const editedNode = useSelector(selectEditedNode);
    const shouldDropOutboundEdges = useSelector(selectShouldDropOutboundEdges);
    const { triggerNode } = useTriggerEventNode();
    const [formSubmited, setFormSubmited] = useToggle(false);
    const [canSubmit, setCanSubmit] = useState(true);

    const flattenedFields = useMemo(() => {
        if (type === NodeType.UpdateContact) {
            return fields;
        }

        return !!fields ? Object.assign({}, ...fields.map((f) => ({ [f.name as string]: f.value }))) : {};
    }, [fields, type]);

    const debounceDispatchToStore = useMemo(
        () =>
            // need the debounce 0 in order to get correct data in store
            debounce((_, allFields: FieldData[]): void => {
                setFormSubmited(false);
                // The CancelTask Form Items are top to bottom dependent
                // Update this check as we get more node types that are top to bottom dependent
                if (type === NodeType.CancelTask) {
                    // We need to clear out any fields that are below the touched field
                    // so that we don't have stale data.
                    // This is a necessary hack until we can refactor the form items and
                    // stop using local state and form values.
                    removeAfterTouched(allFields);
                }

                // this filter step was breaking the nested values in the update contact form
                // not sure why we are filtering for all other nodes.
                if (type === NodeType.UpdateContact) {
                    // we are handling the fields in the UpdateContactFormItems
                    return;
                } else {
                    const filteredFields = allFields.filter((x: any) => {
                        // take only parent fields aka first layer. Arrays elements should not be taken since will create duplicates
                        // not even sure why we save the fields, we could split and create enhanced selectors to avoid rerenderings
                        return x.name.length == 1;
                    });
                    dispatch(setFields(filteredFields));
                }
            }, 0),
        [dispatch, setFormSubmited, type]
    );

    const getFormElements = useCallback(
        (type?: string) => {
            switch (type) {
                case NodeType.Trigger: {
                    return <TriggerFormItems form={form} formValues={flattenedFields} />;
                }
                case NodeType.Delay: {
                    return (
                        <DelayFormItems
                            triggeringEvent={triggerNode}
                            formValues={flattenedFields}
                            setCanSubmit={setCanSubmit}
                        />
                    );
                }
                case NodeType.Call: {
                    return (
                        <CallFormItems
                            formValues={flattenedFields}
                            callType={CAMPAIGN_MESSAGE_TYPE_OPTIONS.phone_call}
                        />
                    );
                }
                // this is the Basic Agent SMS Block (orange)
                case NodeType.AgentSMSTask: {
                    return <AgentSMSTask formValues={flattenedFields} />;
                }
                // THIS is the Automated SMS Block
                case NodeType.SMS: {
                    return <SMSFormItems formValues={flattenedFields} callType={CAMPAIGN_MESSAGE_TYPE_OPTIONS.sms} />;
                }

                case NodeType.CancelTask: {
                    return <CancelTaskFormItems formValues={flattenedFields} form={form} />;
                }
                case NodeType.CustomTask: {
                    return <CustomTaskFormItems />;
                }
                case NodeType.Conditional: {
                    return (
                        <MultipleCriteriaConditionalNodeItem
                            triggeringEvent={triggerNode}
                            formValues={flattenedFields}
                        />
                    );
                }
                case NodeType.ABTest: {
                    return <ABTestFormItems form={form} formValues={flattenedFields} setCanSubmit={setCanSubmit} />;
                }
                case NodeType.Webhook: {
                    return <WebhookFormItems triggeringEvent={triggerNode} formValues={flattenedFields} />;
                }
                case NodeType.AssignTargetAgent: {
                    return <AssignTargetAgentFormItems />;
                }
                case NodeType.UpdateTask: {
                    return <UpdateTaskFormItems updateCriteria={flattenedFields?.updateCriteria} />;
                }
                case NodeType.UpdateContact: {
                    return <UpdateContactFormItems></UpdateContactFormItems>;
                }
                case NodeType.Reminder: {
                    return <ReminderFormItems />;
                }
                case NodeType.ScheduledCallback: {
                    return <ScheduledCallbackFormItems />;
                }
                case NodeType.DEMO_EightByEight: {
                    return <EightByEightFormItems />;
                }
                case NodeType.DEMO_RinglessVM: {
                    return <RinglessVMFormItems />;
                }
                case NodeType.DEMO_AgentlessCall: {
                    return <AgentlessCallFormItems />;
                }
                case NodeType.DEMO_VoiceAI: {
                    return <VoiceAIFormItems />;
                }
                case NodeType.DEMO_Genesys: {
                    return <GenesysFormItems />;
                }
                case NodeType.DEMO_Talkdesk: {
                    return <TalkdeskFormItems />;
                }
                case NodeType.DEMO_NICE: {
                    return <NiceFormItems />;
                }
                case NodeType.DEMO_Five9: {
                    return <Five9FormItems />;
                }
                case NodeType.DEMO_SMSAI: {
                    return <SMSAIFormItems />;
                }
                case NodeType.DEMO_CustomTaskAI: {
                    return <CustomAITaskFormItems />;
                }
                case NodeType.DecisionAI: {
                    return <DecisionAIFormItems />;
                }
                case NodeType.DEMO_SendEmail: {
                    return <SendEmailFormItems />;
                }
                case NodeType.DEMO_EmailTask: {
                    return <EmailTaskFormItems />;
                }
            }
        },
        [flattenedFields, form, triggerNode]
    );

    // sanitize fields have to be added as transformation to data
    // from getJourney result in JourneyEditor onCompleted
    // this setFields can break undo
    const sanitizeDeprecatedFields = useCallback(
        (flattenedFields: Record<string, any>) => {
            // initial attempt at fixing deprecated values in old node data, should be done better
            // after we talk about it (https://regalvoice.atlassian.net/browse/RD-1660)
            if (flattenedFields?.conditionalType === "conditionType") {
                flattenedFields.conditionalType = "customEvent";
                dispatch(setFields(unflattenFields(flattenedFields)));
            }
        },
        [dispatch]
    );

    const onFinish = useCallback(
        (data: any) => {
            dispatch(updateNode({ id, data: normalizeIsOneOfPropertyValues(data) }));

            // cleanup orphaned edges if needed
            if (type === NodeType.ABTest) {
                // start computing active variants
                const variants: Record<string, Variant> = data.variants;
                const activeVariants: Array<Variant> = [];

                Object.entries(variants)?.forEach(([_, variant]) => {
                    if (variant.isActive) {
                        activeVariants.push(variant);
                    }
                });
                // end computing active variants
                // computing active variants can be moved in orphanedEdgeHandler
                // node was updated and we have everything in state
                if (!isEmpty(activeVariants)) {
                    dispatch(orphanedEdgeHandler(id, activeVariants));
                }
            }

            if (type === NodeType.DecisionAI) {
                if (shouldDropOutboundEdges) {
                    dispatch(dropOutboundEdges(id));
                }
            }

            // Additional validation for UpdateContact node
            if (type === NodeType.UpdateContact) {
                const {
                    [UpdateContactForm.eventName]: eventName,
                    [UpdateContactForm.propertiesListName]: properties,
                    [UpdateContactForm.profileListName]: profileAttributesList,
                } = data;
                if (
                    updateContactNodeIsInvalid({
                        form,
                        eventName,
                        properties,
                        profileAttributesList,
                    })
                ) {
                    return;
                }
            }

            // only clear once we are done
            dispatch(clearEditedNode());
        },
        [dispatch, form, shouldDropOutboundEdges, id, type]
    );
    const onCancel = useCallback(() => {
        dispatch(clearEditedNode());
    }, [dispatch]);

    useEffect(() => {
        form.setFieldsValue(editedNode?.data);
        // we only want to observe editedNode
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedNode]);

    useEffect(() => {
        sanitizeDeprecatedFields(flattenedFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flattenedFields]);

    const initialValues = useMemo(() => {
        // Because Update Contact node makes heavy usage of redux state instead
        // of form state, passing this parameter causes an infinite loop of re-rendering
        // which makes it unusable.
        //
        // Refactoring in this ticket is way out of scope, given the complexity of the node
        // so this will have to be done some other time, hopefully soon.
        //
        // TODO: refactor Update Contact node to use only form state instead of redux (see Decision AI Node)
        if (type === NodeType.UpdateContact) {
            return undefined;
        }

        return flattenedFields;
    }, [flattenedFields, type]);

    return (
        <div className={clsx(styles.container, "flex-column")} data-testid={`node-editor-${id}`}>
            <PanelTitle onClick={() => onCancel()}>{getTitle(type)}</PanelTitle>
            <Form
                form={form}
                initialValues={initialValues}
                fields={fields}
                onFieldsChange={debounceDispatchToStore as any}
                name={`node-editor-${type}-${id}`}
                layout="vertical"
                requiredMark={false}
                className={clsx(styles.formContainer, "flex-column")}
                onFinish={onFinish}
            >
                <JourneyFormProvider form={form} formSubmited={formSubmited}>
                    <div className={styles.formElements}>{getFormElements(type)}</div>
                    <div className={clsx(styles.actions, "flex-row")}>
                        <RVButton onClick={onCancel} disabled={isViewOnly}>
                            Cancel
                        </RVButton>
                        <RVButton
                            htmlType="submit"
                            type="primary"
                            onClick={() => {
                                setFormSubmited(true);
                            }}
                            disabled={!canSubmit || isViewOnly}
                        >
                            Save
                        </RVButton>
                    </div>
                </JourneyFormProvider>
            </Form>
        </div>
    );
}
